import { twMerge } from 'tailwind-merge';

/**
 * Normalized WBS outline level (1 = project summary / root name in MS Project).
 */
export function normalizeWbsLevel(raw: unknown): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(Math.floor(n), 12);
}

/**
 * Coloured palette starts at WBS 2. WBS 1 is the project name — neutral only.
 * Tier 0 = outline 2, tier 1 = outline 3, …
 */
export function paletteTierIndex(outlineLevel: number): number | null {
  const L = normalizeWbsLevel(outlineLevel);
  if (L <= 1) return null;
  return Math.min(L - 2, TABLE_ROW_BASE.length - 1);
}

/** WBS 1 — project / root (Sovereign summary: candlelight + champagne rail). */
const TABLE_ROW_PROJECT = [
  'border-l-[4px] border-l-[#C9A961] bg-gradient-to-r from-[#F8F2E2] via-[#FCFBF6] to-white',
].join(' ');

const LEVEL_BADGE_PROJECT =
  'bg-[#0B3B2E] text-[#F8F2E2] ring-1 ring-[rgba(201,169,97,0.45)] shadow-sm';

const TIMELINE_LABEL_PROJECT = 'border-l-[3px] border-l-[#C9A961] bg-[#F8F2E2]';

const BAR_PROJECT_SUMMARY = 'bg-[rgba(201,169,97,0.30)] ring-[rgba(201,169,97,0.55)]';
const BAR_PROJECT_LEAF = 'bg-[rgba(31,90,71,0.18)] ring-[rgba(31,90,71,0.30)]';

/**
 * Row styling from WBS 2 onward (Sovereign Emerald ramp). Shallow levels (summaries / parents)
 * read warm candlelight with a champagne rail; deeper levels (leaf / subtasks) fade to near-white
 * with a thin bottle/hairline rail. Index 0 = WBS 2, index 1 = WBS 3, …
 */
const TABLE_ROW_BASE: readonly string[] = [
  // WBS 2 — phase / major block (summary)
  'border-l-[4px] border-l-[#C9A961] bg-gradient-to-r from-[#F8F2E2] via-[rgba(201,169,97,0.10)] to-white',
  // WBS 3 (summary)
  'border-l-[4px] border-l-[#C9A961] bg-gradient-to-r from-[rgba(201,169,97,0.12)] via-[#FCFBF6] to-white',
  // WBS 4
  'border-l-[3px] border-l-[#1F5A47] bg-gradient-to-r from-[rgba(31,90,71,0.08)] via-[#FCFBF6] to-white',
  // WBS 5
  'border-l-[3px] border-l-[#1F5A47] bg-gradient-to-r from-[rgba(31,90,71,0.05)] via-[#FCFBF6] to-white',
  // WBS 6
  'border-l-[2px] border-l-[#339274] bg-gradient-to-r from-[rgba(51,146,116,0.05)] via-[#FCFBF6] to-white',
  // WBS 7
  'border-l-[2px] border-l-[rgba(31,90,71,0.45)] bg-[#FCFBF6]',
  // WBS 8+
  'border-l-[2px] border-l-[rgba(11,59,46,0.30)] bg-[#FCFBF6]',
  'border-l-[2px] border-l-[rgba(11,59,46,0.24)] bg-[#FCFBF6]',
  'border-l-[2px] border-l-[rgba(11,59,46,0.18)] bg-white',
  'border-l-[2px] border-l-[rgba(11,59,46,0.16)] bg-white',
  'border-l-[2px] border-l-[rgba(11,59,46,0.12)] bg-white',
  'border-l-[2px] border-l-[rgba(11,59,46,0.10)] bg-white',
];

const LEVEL_BADGE: readonly string[] = [
  'bg-[#0B3B2E] text-[#F8F2E2] ring-1 ring-[rgba(11,59,46,0.20)]',
  'bg-[#1F5A47] text-[#F8F2E2] ring-1 ring-[rgba(31,90,71,0.15)]',
  'bg-[#339274] text-white ring-1 ring-[rgba(51,146,116,0.15)]',
  'bg-[rgba(51,146,116,0.85)] text-white ring-1 ring-[rgba(51,146,116,0.20)]',
  'bg-[#C9A961] text-[#2A1F17] ring-1 ring-[rgba(201,169,97,0.30)]',
  'bg-[rgba(201,169,97,0.55)] text-[#2A1F17] ring-1 ring-[rgba(201,169,97,0.35)]',
  'bg-[rgba(201,169,97,0.32)] text-[#2A1F17] ring-1 ring-[rgba(201,169,97,0.30)]',
  'bg-[rgba(11,59,46,0.16)] text-[#2A1F17] ring-1 ring-[rgba(11,59,46,0.16)]',
  'bg-[rgba(11,59,46,0.12)] text-[#2A1F17] ring-1 ring-[rgba(11,59,46,0.14)]',
  'bg-[#F2EADB] text-[#2A1F17] ring-1 ring-[rgba(11,59,46,0.12)]',
  'bg-[#F8F2E2] text-[#7C8A82] ring-1 ring-[rgba(11,59,46,0.10)]',
  'bg-[#FCFBF6] text-[#7C8A82] ring-1 ring-[rgba(11,59,46,0.10)]',
];

const TIMELINE_LABEL: readonly string[] = [
  'border-l-[4px] border-l-[#C9A961] bg-[#F8F2E2]',
  'border-l-[4px] border-l-[#C9A961] bg-[rgba(201,169,97,0.12)]',
  'border-l-[3px] border-l-[#1F5A47] bg-[rgba(31,90,71,0.08)]',
  'border-l-[3px] border-l-[#1F5A47] bg-[rgba(31,90,71,0.05)]',
  'border-l-[3px] border-l-[#339274] bg-[rgba(51,146,116,0.05)]',
  'border-l-[2px] border-l-[rgba(31,90,71,0.45)] bg-[#FCFBF6]',
  'border-l-[2px] border-l-[rgba(11,59,46,0.30)] bg-[#FCFBF6]',
  'border-l-[2px] border-l-[rgba(11,59,46,0.24)] bg-[#FCFBF6]',
  'border-l-[2px] border-l-[rgba(11,59,46,0.18)] bg-white',
  'border-l-[2px] border-l-[rgba(11,59,46,0.16)] bg-[#FCFBF6]',
  'border-l-[2px] border-l-[rgba(11,59,46,0.12)] bg-[#FCFBF6]',
  'border-l-[2px] border-l-[rgba(11,59,46,0.10)] bg-white',
];

const BAR_SUMMARY_RING: readonly string[] = [
  'bg-[rgba(201,169,97,0.30)] ring-[rgba(201,169,97,0.55)]',
  'bg-[rgba(201,169,97,0.26)] ring-[rgba(201,169,97,0.50)]',
  'bg-[rgba(201,169,97,0.22)] ring-[rgba(201,169,97,0.45)]',
  'bg-[rgba(201,169,97,0.18)] ring-[rgba(201,169,97,0.40)]',
  'bg-[rgba(201,169,97,0.16)] ring-[rgba(201,169,97,0.36)]',
  'bg-[rgba(201,169,97,0.14)] ring-[rgba(201,169,97,0.32)]',
  'bg-[rgba(201,169,97,0.12)] ring-[rgba(201,169,97,0.28)]',
  'bg-[rgba(11,59,46,0.10)] ring-[rgba(11,59,46,0.18)]',
  'bg-[rgba(11,59,46,0.09)] ring-[rgba(11,59,46,0.16)]',
  'bg-[rgba(11,59,46,0.08)] ring-[rgba(11,59,46,0.14)]',
  'bg-[rgba(11,59,46,0.07)] ring-[rgba(11,59,46,0.12)]',
  'bg-[rgba(11,59,46,0.06)] ring-[rgba(11,59,46,0.10)]',
];

const BAR_LEAF: readonly string[] = [
  'bg-[rgba(31,90,71,0.22)] ring-[rgba(31,90,71,0.40)]',
  'bg-[rgba(31,90,71,0.20)] ring-[rgba(31,90,71,0.36)]',
  'bg-[rgba(51,146,116,0.20)] ring-[rgba(51,146,116,0.36)]',
  'bg-[rgba(51,146,116,0.16)] ring-[rgba(51,146,116,0.32)]',
  'bg-[rgba(51,146,116,0.13)] ring-[rgba(51,146,116,0.28)]',
  'bg-[rgba(51,146,116,0.11)] ring-[rgba(51,146,116,0.24)]',
  'bg-[rgba(51,146,116,0.09)] ring-[rgba(51,146,116,0.20)]',
  'bg-[rgba(11,59,46,0.10)] ring-[rgba(11,59,46,0.18)]',
  'bg-[rgba(11,59,46,0.09)] ring-[rgba(11,59,46,0.16)]',
  'bg-[rgba(11,59,46,0.08)] ring-[rgba(11,59,46,0.14)]',
  'bg-[rgba(11,59,46,0.07)] ring-[rgba(11,59,46,0.12)]',
  'bg-[rgba(11,59,46,0.06)] ring-[rgba(11,59,46,0.10)]',
];

const LEGEND_SWATCH: readonly string[] = [
  'bg-[#C9A961]',
  'bg-[#C9A961]',
  'bg-[#1F5A47]',
  'bg-[#1F5A47]',
  'bg-[#339274]',
  'bg-[rgba(31,90,71,0.45)]',
  'bg-[rgba(11,59,46,0.30)]',
  'bg-[rgba(11,59,46,0.24)]',
  'bg-[rgba(11,59,46,0.18)]',
  'bg-[rgba(11,59,46,0.16)]',
  'bg-[rgba(11,59,46,0.12)]',
  'bg-[rgba(11,59,46,0.10)]',
];

export function getWbsTableRowClasses(
  level: number,
  opts: { selected: boolean; interactive?: boolean; linkFlash?: 'source' | 'target' }
): string {
  const tier = paletteTierIndex(level);
  const base = tier === null ? TABLE_ROW_PROJECT : TABLE_ROW_BASE[tier] ?? TABLE_ROW_BASE[TABLE_ROW_BASE.length - 1];
  const interactive = opts.interactive !== false;
  return twMerge(
    'border-b border-[rgba(11,59,46,0.10)] transition-colors last:border-b-0',
    interactive ? 'cursor-pointer' : '',
    base,
    opts.selected ? 'bg-[rgba(201,169,97,0.16)] ring-2 ring-inset ring-[rgba(201,169,97,0.55)] shadow-[inset_0_0_0_1px_rgba(201,169,97,0.20)] z-[1] relative' : '',
    opts.linkFlash === 'source'
      ? 'z-[2] ring-2 ring-inset ring-[#C9A961] shadow-[inset_0_0_0_1px_rgba(201,169,97,0.45)] motion-safe:animate-pulse'
      : '',
    opts.linkFlash === 'target'
      ? 'z-[2] ring-[3px] ring-inset ring-[#1F5A47] shadow-[0_0_0_4px_rgba(31,90,71,0.25)] motion-safe:animate-pulse'
      : '',
    !opts.selected && interactive && !opts.linkFlash ? 'hover:bg-[rgba(11,59,46,0.05)]' : '',
    !opts.selected && interactive && opts.linkFlash ? 'hover:bg-[rgba(11,59,46,0.04)]' : ''
  );
}

export function getWbsLevelBadgeClasses(level: number): string {
  const L = normalizeWbsLevel(level);
  if (L <= 1) {
    return twMerge(
      'inline-flex min-w-[2rem] items-center justify-center rounded-md px-2 py-0.5 font-mono text-xs font-bold tabular-nums',
      LEVEL_BADGE_PROJECT
    );
  }
  const tier = paletteTierIndex(L);
  const i = tier ?? 0;
  return twMerge(
    'inline-flex min-w-[2rem] items-center justify-center rounded-md px-2 py-0.5 font-mono text-xs font-bold tabular-nums shadow-sm',
    LEVEL_BADGE[i] ?? LEVEL_BADGE[LEVEL_BADGE.length - 1]
  );
}

export function getWbsTimelineLabelClasses(level: number, selected: boolean): string {
  const tier = paletteTierIndex(level);
  const base = tier === null ? TIMELINE_LABEL_PROJECT : TIMELINE_LABEL[tier] ?? TIMELINE_LABEL[TIMELINE_LABEL.length - 1];
  return twMerge(
    'flex w-full flex-row items-stretch border-b border-[rgba(11,59,46,0.10)] pl-0 pr-3 text-left text-xs font-medium transition',
    base,
    selected ? 'ring-2 ring-inset ring-[rgba(201,169,97,0.55)] z-[1]' : 'hover:bg-[rgba(11,59,46,0.05)]'
  );
}

export function getWbsTimelineBarShellClasses(level: number, isSummary: boolean): string {
  const tier = paletteTierIndex(level);
  if (tier === null) {
    const shell = isSummary ? BAR_PROJECT_SUMMARY : BAR_PROJECT_LEAF;
    return twMerge('absolute overflow-hidden rounded-[5px] ring-1 shadow-sm', shell);
  }
  const shell = isSummary ? BAR_SUMMARY_RING[tier] : BAR_LEAF[tier];
  return twMerge('absolute overflow-hidden rounded-[5px] ring-1 shadow-sm', shell);
}

/** Flat engraved-ink progress fill — Sovereign register, no gradients on the Gantt. */
export function getWbsTimelineProgressFill(level: number): string {
  const tier = paletteTierIndex(level);
  if (tier === null) return 'bg-[#C9A961]';
  if (tier <= 1) return 'bg-[#1F5A47]';
  if (tier <= 3) return 'bg-[#339274]';
  if (tier <= 5) return 'bg-[rgba(51,146,116,0.75)]';
  return 'bg-[rgba(11,59,46,0.45)]';
}

/** Legend: WBS 2–7 (tier colours). L1 is project — use `getWbsLegendSwatchClasses(1)`. */
export const WBS_LEGEND_LEVELS = [2, 3, 4, 5, 6, 7] as const;

export function getWbsLegendSwatchClasses(level: number): string {
  const L = normalizeWbsLevel(level);
  if (L <= 1) {
    return twMerge('h-2.5 w-6 rounded-sm ring-1 ring-black/10', 'bg-[#C9A961]');
  }
  const tier = paletteTierIndex(L);
  const i = tier ?? 0;
  return twMerge('h-2.5 w-6 rounded-sm ring-1 ring-black/10', LEGEND_SWATCH[i] ?? LEGEND_SWATCH[0]);
}

/** Width of one WBS tree guide column (px). */
export const WBS_INDENT_STEP_PX = 22;

/** Number of ancestor steps below project root (WBS 1 → 0). */
export function getWbsDepthStepCount(level: number): number {
  return Math.max(0, normalizeWbsLevel(level) - 1);
}

/** Total horizontal space used by tree guides for a row. */
export function getWbsTreeGutterWidthPx(level: number): number {
  return getWbsDepthStepCount(level) * WBS_INDENT_STEP_PX;
}
